import axios from 'axios';

// Get API URL from environment variable or default to /api
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

console.log('API Base URL:', API_BASE_URL);

// Configure axios defaults
axios.defaults.withCredentials = true; // Important for cookie-based auth

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear any local auth state if needed
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  register: (username, password, email) =>
    api.post('/auth/register', { username, password, email }),
  
  login: (username, password) =>
    api.post('/auth/login', { username, password }),
  
  logout: () =>
    api.post('/auth/logout'),
  
  getCurrentUser: () =>
    api.get('/auth/me'),
  
  checkAuth: () =>
    api.get('/auth/check'),
  
  // Admin endpoints
  getUsers: () =>
    api.get('/admin/users'),
  
  updateUserQuota: (username, tier) =>
    api.post(`/admin/users/${username}/quota`, { tier }),
};

// API Keys
export const apiKeysAPI = {
  list: () =>
    api.get('/auth/api-keys'),
  
  create: (name, expires_days) =>
    api.post('/auth/api-keys', { name, expires_days }),
  
  delete: (keyId) =>
    api.delete(`/auth/api-keys/${keyId}`),
  
  revoke: (keyId) =>
    api.post(`/auth/api-keys/${keyId}/revoke`),
};

// File Management
export const filesAPI = {
  upload: (files, isPublic) => {
    const formData = new FormData();
    // Support both single file and multiple files
    if (Array.isArray(files)) {
      files.forEach(file => {
        formData.append('file', file);
      });
    } else {
      formData.append('file', files);
    }
    formData.append('is_public', isPublic);
    return api.post('/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  getMyFiles: (visibility) =>
    api.get('/files/my-files', { params: { visibility } }),
  
  getFileInfo: (fileId) =>
    api.get(`/files/${fileId}`),
  
  downloadFile: (fileId) =>
    `${API_BASE_URL}/files/${fileId}/download`,
  
  updateVisibility: (fileId, isPublic) =>
    api.put(`/files/${fileId}/visibility`, { is_public: isPublic }),
  
  deleteFile: (fileId) =>
    api.delete(`/files/${fileId}`),
  
  getPublicFiles: (search) =>
    api.get('/files/public', { params: { search } }),
  
  downloadPublicFile: (username, fileId) =>
    `${API_BASE_URL}/files/public/${username}/${fileId}/download`,
};

// Storage
export const storageAPI = {
  getStats: () =>
    api.get('/storage/stats'),
};

// APK Builds
export const buildsAPI = {
  list: () =>
    api.get('/builds'),
  
  download: (filename) =>
    `${API_BASE_URL}/builds/${filename}/download`,
  
  getStats: () =>
    api.get('/builds/stats'),
};

// Health Check
export const healthAPI = {
  check: () =>
    api.get('/health'),
};

// Projects
export const projectsAPI = {
  // Get all projects (public if not auth, user's if auth)
  list: () =>
    api.get('/projects'),
  
  // Get all public projects (for browsing)
  listPublic: () =>
    api.get('/projects/public'),
  
  // Get current user's projects
  getMyProjects: () =>
    api.get('/projects/my'),
  
  // Get single project by ID
  get: (projectId) =>
    api.get(`/projects/${projectId}`),
  
  // Get project by slug (public pages)
  getBySlug: (slug) =>
    api.get(`/projects/slug/${slug}`),
  
  // Create new project
  create: (projectData) =>
    api.post('/projects', projectData),
  
  // Update project
  update: (projectId, projectData) =>
    api.put(`/projects/${projectId}`, projectData),
  
  // Delete project
  delete: (projectId) =>
    api.delete(`/projects/${projectId}`),
  
  // Add download to project
  addDownload: (projectId, downloadData) =>
    api.post(`/projects/${projectId}/downloads`, downloadData),
  
  // Remove download from project
  removeDownload: (projectId, fileId) =>
    api.delete(`/projects/${projectId}/downloads/${fileId}`),
  
  // Update download metadata
  updateDownload: (projectId, fileId, data) =>
    api.put(`/projects/${projectId}/downloads/${fileId}/update`, data),
  
  // Get project stats
  getStats: (projectId) =>
    api.get(`/projects/${projectId}/stats`),
  
  // Collaborators
  addCollaborator: (projectId, data) =>
    api.post(`/projects/${projectId}/collaborators`, data),
  
  listCollaborators: (projectId) =>
    api.get(`/projects/${projectId}/collaborators`),
  
  removeCollaborator: (projectId, username) =>
    api.delete(`/projects/${projectId}/collaborators/${username}`),
  
  // Transfer ownership
  transferOwnership: (projectId, newOwner) =>
    api.post(`/projects/${projectId}/transfer-ownership`, { new_owner: newOwner }),
  
  // Download project file
  downloadProjectFile: (projectId, fileId) =>
    `${API_BASE_URL}/projects/${projectId}/downloads/${fileId}/download`,
  
  // Get public page URL
  getPublicUrl: (slug) =>
    `${window.location.origin}/p/${slug}`,
  
  // Feed
  addFeedPost: (projectId, content) =>
    api.post(`/projects/${projectId}/feed`, { content }),
  
  getFeedPosts: (projectId, limit = 10, offset = 0) =>
    api.get(`/projects/${projectId}/feed`, { params: { limit, offset } }),
  
  updateFeedPost: (projectId, postId, content) =>
    api.put(`/projects/${projectId}/feed/${postId}`, { content }),
  
  deleteFeedPost: (projectId, postId) =>
    api.delete(`/projects/${projectId}/feed/${postId}`),
  
  // Backers
  getBackers: (projectId) =>
    api.get(`/projects/${projectId}/backers`),
  
  backProject: (projectId, data = {}) =>
    api.post(`/projects/${projectId}/backers`, data),
  
  updateBacking: (projectId, backerId, data) =>
    api.put(`/projects/${projectId}/backers/${backerId}`, data),
  
  unbackProject: (projectId, backerId) =>
    api.delete(`/projects/${projectId}/backers/${backerId}`),
  
  getMyBackedProjects: () =>
    api.get('/my/backed-projects'),

  // Crypto Payments
  submitPayment: (projectId, data) =>
    api.post(`/projects/${projectId}/payment`, data),

  verifyPayment: (projectId, backerId) =>
    api.post(`/projects/${projectId}/payments/${backerId}/verify`),

  getSales: (projectId) =>
    api.get(`/projects/${projectId}/sales`),

  // HD Wallets & Invoices
  generateWallet: (projectId, chain) =>
    api.post(`/projects/${projectId}/wallets/${chain}/generate`),

  getWallets: (projectId) =>
    api.get(`/projects/${projectId}/wallets`),

  getWalletSeed: (projectId, chain) =>
    api.get(`/projects/${projectId}/wallets/${chain}/seed`),

  createInvoice: (projectId, data) =>
    api.post(`/projects/${projectId}/invoices`, data),

  checkInvoiceStatus: (projectId, invoiceId) =>
    api.get(`/projects/${projectId}/invoices/${invoiceId}/status`),

  // Screenshots
  addScreenshot: (projectId, data) =>
    api.post(`/projects/${projectId}/screenshots`, data),
  
  uploadScreenshot: (projectId, file, caption = '') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('caption', caption);
    return api.post(`/projects/${projectId}/screenshots/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  getScreenshots: (projectId) =>
    api.get(`/projects/${projectId}/screenshots`),
  
  updateScreenshot: (projectId, screenshotId, data) =>
    api.put(`/projects/${projectId}/screenshots/${screenshotId}`, data),
  
  removeScreenshot: (projectId, screenshotId) =>
    api.delete(`/projects/${projectId}/screenshots/${screenshotId}`),
  
  // Get screenshot file URL (for uploaded screenshots)
  getScreenshotUrl: (projectId, filename) =>
    `${API_BASE_URL}/projects/${projectId}/screenshots/file/${filename}`,
  
  // Project Icon
  uploadIcon: (projectId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/projects/${projectId}/icon/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  deleteIcon: (projectId) =>
    api.delete(`/projects/${projectId}/icon`),
  
  getIconUrl: (projectId, filename) =>
    `${API_BASE_URL}/projects/${projectId}/icon/${filename}`,
  
  // Videos
  addVideo: (projectId, data) =>
    api.post(`/projects/${projectId}/videos`, data),
  
  getVideos: (projectId) =>
    api.get(`/projects/${projectId}/videos`),
  
  updateVideo: (projectId, videoId, data) =>
    api.put(`/projects/${projectId}/videos/${videoId}`, data),
  
  removeVideo: (projectId, videoId) =>
    api.delete(`/projects/${projectId}/videos/${videoId}`),
};

export default api;
